import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Volume2, Mic, Coins, ShieldCheck, Github, Zap } from "lucide-react";

const MODELS = [
  { label: "OpenAI GPT-5.4 Nano", note: "Fast + cheap" },
  { label: "Claude Opus 5", note: "Anthropic flagship" },
  { label: "Gemini 3.1 Flash Lite", note: "Google's economy tier" },
  { label: "Grok 4.5", note: "xAI current model" },
  { label: "DeepSeek R1", note: "Advanced reasoning" },
];

export default function About() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="about-page">
      <div className="glass-header sticky top-0 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 h-20">
          <button onClick={() => nav("/")} className="btn-ghost focus-ring" data-testid="about-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-[0.15em]">Back</span>
          </button>
          <div className="label-eyebrow">about</div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="label-eyebrow mb-3">no subscription · pay only when you use it</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
          Pro AI models,<br />
          <span className="text-white/50">without the monthly bill.</span>
        </h1>
        <p className="text-white/70 text-[15px] leading-relaxed max-w-2xl mb-6">
          wangram.com is a pay-as-you-go multi-model AI comparison tool.
          Skip the $20/month subscription per provider — just top up a small
          bag of coins and use OpenAI GPT-5.2, Claude Sonnet 4.5, Gemini 2.5 Pro,
          Grok 4.3, and DeepSeek v3 whenever you actually have a question worth asking.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mb-14" data-testid="value-props">
          <ValueProp title="No subscription" body="Never billed if you don't use it." />
          <ValueProp title="Per-query pricing" body="1 coin per 5-model compare. Starts at $0.99." />
          <ValueProp title="Coins never expire" body="Top up once, keep them forever." />
        </div>

        <Section title="How it works" icon={Sparkles}>
          <Step n="1" title="Ask">
            Type a question into the search bar — or dictate it with the microphone
            (powered by OpenAI Whisper).
          </Step>
          <Step n="2" title="Compare">
            Your prompt is fanned out to all five models in parallel via OpenRouter.
            Answers appear stacked below, each in its own panel with latency shown.
          </Step>
          <Step n="3" title="Pin the best">
            Click the 👍 on the panel you find most useful. Your win-rate leaderboard
            updates so you learn which model works best for you over time.
          </Step>
          <Step n="4" title="Listen">
            Every successful answer has a Listen button that reads it aloud with
            OpenAI's TTS (you can pick your preferred voice on the Profile page).
          </Step>
        </Section>

        <Section title="The five models" icon={Zap}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {MODELS.map((m) => (
              <div key={m.label} className="p-4 rounded-md border border-white/10 bg-white/[0.02]">
                <div className="font-display font-black text-[14px] tracking-tight">{m.label}</div>
                <div className="text-[12px] text-white/50 mt-1">{m.note}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Coins & pricing" icon={Coins}>
          <p className="text-white/70 text-[14px] leading-relaxed mb-4">
            One coin = one 5-model comparison. Signup gets you <span className="font-medium text-white">2 free coins</span>.
            After that, top up whenever you want — packs never expire and there's no subscription.
          </p>
          <div className="p-4 rounded-md border border-white/10 bg-white/[0.03] text-[13px] text-white/80">
            <div className="flex items-baseline justify-between"><span>Mini Pack</span><span className="font-mono-p">$0.99 → 2 coins</span></div>
            <div className="flex items-baseline justify-between mt-2"><span>Value Pack <span className="text-white/40 label-eyebrow ml-1">best value</span></span><span className="font-mono-p">$10 → 22 coins</span></div>
          </div>
          <div className="mt-4 text-[12px] text-white/50">
            If more than 2 of the 5 models fail on a query, your coin is refunded automatically.
          </div>
          <Link to="/pricing" className="btn-pill focus-ring mt-6" data-testid="about-buy-coins">
            <Coins className="w-4 h-4" />
            Buy coins
          </Link>
        </Section>

        <Section title="Voice input & output" icon={Mic}>
          <p className="text-white/70 text-[14px] leading-relaxed">
            The mic button in the search bar transcribes your voice with OpenAI Whisper.
            The Listen button on each panel reads answers back to you with OpenAI TTS —
            <Volume2 className="w-3.5 h-3.5 inline mx-1" />
            you can pick your voice on the <Link to="/profile" className="underline hover:text-white">Profile page</Link>.
          </p>
        </Section>

        <Section title="Privacy" icon={ShieldCheck}>
          <p className="text-white/70 text-[14px] leading-relaxed">
            Only you can see your own search history, pins, and purchases. Your prompts
            are forwarded to model providers via OpenRouter — no data is sold or shared
            with third parties beyond what's required to answer your question. You can
            delete your account and all its data from the Profile page at any time.
          </p>
        </Section>

        <div className="mt-16 pt-8 border-t border-white/10 flex items-center justify-between text-[12px] text-white/40">
          <div className="flex items-center gap-2">
            <Github className="w-3.5 h-3.5" />
            wangram.com v1.0
          </div>
          <div>Built with FastAPI · React · OpenRouter</div>
        </div>
      </div>
    </div>
  );
}

function ValueProp({ title, body }) {
  return (
    <div className="p-4 rounded-md border border-white/10 bg-white/[0.02]">
      <div className="font-display font-black text-[14px] tracking-tight mb-1">{title}</div>
      <div className="text-[12px] text-white/55 leading-relaxed">{body}</div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="mt-14">
      <div className="flex items-center gap-2 mb-5">
        {Icon && <Icon className="w-4 h-4 text-white/70" />}
        <h2 className="label-eyebrow text-white/80">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-4 mb-5">
      <div className="w-8 h-8 flex-shrink-0 rounded-full border border-white/15 flex items-center justify-center font-display font-black text-[13px] text-white/70">
        {n}
      </div>
      <div>
        <div className="font-display font-black text-[15px] tracking-tight mb-1">{title}</div>
        <div className="text-[13px] text-white/60 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
